import {
  Box,
  Center,
  Container,
  Group,
  Loader,
  SegmentedControl,
  Stack,
  Text,
} from "@mantine/core";
import { Alert, Button, EmptyState } from "@xplatform/design-system";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { MODULES, type Module } from "@models/releaseNotes";
import { useReleaseNotes } from "@hooks/useReleaseNotes";
import { ReleaseNoteCard } from "@components/ReleaseNoteCard";

const ALL = "all";

function isModule(value: string | null): value is Module {
  return value !== null && (MODULES as readonly string[]).includes(value);
}

/**
 * Every released version, newest first.
 *
 * A single reverse-chronological feed rather than sections per product. Readers
 * come here to find out what shipped and when; grouping by product buries the
 * newest release of one product under the older history of another. The product
 * is a filter and a badge, not the outline.
 *
 * The API already returns the list ordered by release date descending, so this
 * never re-sorts. Sorting on the version string would put 2026.10 below 2026.9.
 */
export const ReleasesPage = () => {
  const { t } = useTranslation();
  // Filter state lives in the URL so a support conversation can link straight
  // to one product's history.
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get("module");
  const selected = isModule(raw) ? raw : undefined;

  const { data, loading, error, reload } = useReleaseNotes(selected);

  const filterOptions = useMemo(
    () => [
      { value: ALL, label: t("releases.page.allModules") },
      ...MODULES.map((module) => ({
        value: module,
        label: t(`releases.module.${module}`),
      })),
    ],
    [t],
  );

  const handleFilter = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === ALL) next.delete("module");
    else next.set("module", value);
    setSearchParams(next, { replace: true });
  };

  return (
    <Container size="lg" pb="4rem" className="releases-page">
      <Box className="releases-page__hero" py="xl">
        <Text size="xl" fw={800} component="h1">
          {t("releases.page.title")}
        </Text>
        <Text size="md" c="dimmed">
          {t("releases.page.description")}
        </Text>
      </Box>

      <Group justify="space-between" align="center" mb="lg" wrap="wrap">
        <SegmentedControl
          value={selected ?? ALL}
          onChange={handleFilter}
          data={filterOptions}
          size="sm"
        />
        {data && data.length > 0 && (
          <Text size="sm" c="dimmed">
            {t("releases.page.count", { count: data.length })}
          </Text>
        )}
      </Group>

      {loading && (
        <Center mih={240} aria-busy="true" aria-live="polite">
          <Group gap="sm">
            <Loader size="md" />
            <Text size="md">{t("common.loading")}</Text>
          </Group>
        </Center>
      )}

      {/* An error state, never a blank page: a broken backend must stay
          distinguishable from a product with no releases yet. */}
      {!loading && error && (
        <Alert color="red" title={t("releases.page.errorTitle")}>
          <Stack gap="sm" align="flex-start">
            <Text size="sm">{t("releases.page.errorBody")}</Text>
            <Button size="xs" variant="light" onClick={reload}>
              {t("common.retry")}
            </Button>
          </Stack>
        </Alert>
      )}

      {!loading && !error && data && data.length === 0 && (
        <EmptyState
          title={t("releases.page.emptyTitle")}
          description={t("releases.page.emptyBody")}
        />
      )}

      {!loading && !error && data && data.length > 0 && (
        <Stack gap="lg">
          {data.map((note) => (
            <ReleaseNoteCard key={`${note.module}-${note.version}`} note={note} />
          ))}
        </Stack>
      )}
    </Container>
  );
};
